import { Loader } from '@opendoor/bricks';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { createSelector } from 'reselect';

import * as sessionSelectors from '@opendoor/shared-fe/build/store/session/selectors';
import GenericError from '../components/common/GenericError';
import { IRequestStatus } from '../declarations';
import parseQueryString from '../helpers/parseQueryString';
import * as resaleClosingDashboardActions from '../store/resaleClosingDashboard/actions';
import * as resaleClosingDashboardSelectors from '../store/resaleClosingDashboard/selectors';

export default function resaleClosingDashboardLoader(ComposedComponent: React.ComponentType) {
  interface IProps extends RouteComponentProps<{ listingOfferId: string }> {
    claimListingOfferByTokenRequestStatus: IRequestStatus;
    listingOfferError: string;
    listingOfferIsLoading: boolean;
    loggedIn: boolean;
    maybeFetchListingOffer: (listingOfferId: string, token?: string) => void;
  }

  class ListingOfferLoader extends React.Component<IProps> {
    public componentDidMount() {
      const { location, match, maybeFetchListingOffer } = this.props;
      const { token } = parseQueryString(location.search);
      maybeFetchListingOffer(match.params.listingOfferId, token);
    }

    public componentWillReceiveProps(nextProps: IProps) {
      this.maybeRedirectToLogin(nextProps);
    }

    public maybeRedirectToLogin(nextProps: IProps) {
      const { history, location, loggedIn } = nextProps;

      if (!loggedIn && this.getErrorMessage(nextProps)) {
        history.push(
          '/login?' +
            `redirectTo=${encodeURIComponent(location.pathname + location.search)}&` +
            'config=resaleClosingDashboard',
        );
      }
    }

    public isLoading() {
      const { claimListingOfferByTokenRequestStatus, listingOfferIsLoading } = this.props;
      return claimListingOfferByTokenRequestStatus.isLoading || listingOfferIsLoading;
    }

    public getErrorMessage(nextProps?: IProps) {
      const { claimListingOfferByTokenRequestStatus, listingOfferError, loggedIn } =
        nextProps || this.props;
      const error = claimListingOfferByTokenRequestStatus.error || listingOfferError;
      if (!error) {
        return undefined;
      }

      if (!loggedIn) {
        return error;
      }

      return error.startsWith('404') || error.startsWith('401')
        ? 'Sorry, this is an invalid link.'
        : error;
    }

    public render() {
      const errorMessage = this.getErrorMessage();
      const isLoading = this.isLoading();

      if (!isLoading && !errorMessage) {
        return <ComposedComponent {...this.props} />;
      }

      return (
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              {errorMessage ? (
                <GenericError
                  errorContent={
                    <div>
                      <p>{errorMessage}</p>
                      <p>Double check the link or contact support for a new link.</p>
                    </div>
                  }
                />
              ) : (
                <Loader />
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  const mapStateToProps = createSelector(
    [
      resaleClosingDashboardSelectors.claimListingOfferByTokenRequestStatus,
      resaleClosingDashboardSelectors.listingOfferError,
      resaleClosingDashboardSelectors.listingOfferIsLoading,
      sessionSelectors.loggedIn,
    ],
    (
      claimListingOfferByTokenRequestStatus: IRequestStatus,
      listingOfferError: string,
      listingOfferIsLoading: boolean,
      loggedIn: boolean,
    ) => ({
      claimListingOfferByTokenRequestStatus,
      listingOfferError,
      listingOfferIsLoading,
      loggedIn,
    }),
  );

  const mapDispatchToProps = {
    maybeFetchListingOffer: resaleClosingDashboardActions.maybeFetchListingOffer,
  };

  return withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(ListingOfferLoader),
  );
}
